
import firebase from 'firebase/compat/app'
import 'firebase/compat/functions'
import 'firebase/compat/auth'
import { showSnackbar } from '../globalActions'
import Mixins from '@/shared'

import { MatchResult } from '@/interfaces/matchResult'
import { MatchEvent } from '@/interfaces/matchEvent'

interface MatchLogSource {
  logId?: string
  eventType?: string
  matchTime?: number
  order?: number
  hidden?: boolean
  edited?: boolean
  deleted?: boolean
  playerId?: string
  letter?: string
  teamId?: string
  _soloKey?: string
}

interface MatchLogRow {
  id: string
  timeLabel: string
  order?: number
  team: string
  player: string
  eventLabel: string
  score: string
  status: string
  logId: string
  revisions?: MatchLogRow[]
}

export default {
  name: 'MatchReport',
  mixins: [Mixins],
  props: {
    match: {
      type: Object,
      default: function () {
        return { id: '', gender: '', teams: [] }
      }
    }
  },
  data () {
    return {
      layout: {
        fluid: true
      },
      matchResult: {},
      players: [],
      teams: [] as Array<unknown>,
      score: [0, 0],
      loading: false,
      saving: false,
      loadingStandings: false,
      downloadString: null,
      matchReportDialog: false,
      matchLogDialog: false,
      expandedMatchLog: [] as Array<Record<string, unknown>>,
      liveEventLog: []
    }
  },
  computed: {
    homeTeamShortName (): string {
      if ('shortName' in this.match.homeTeam) {
        return this.match.homeTeam.shortName
      }
      return this.match.homeTeam.name.substring(0, 4)
    },
    awayTeamShortName (): string {
      if ('shortName' in this.match.awayTeam) {
        return this.match.awayTeam.shortName
      }
      return this.match.awayTeam.name.substring(0, 4)
    },
    homeTeamScore (): number {
      if (
        'home' in this.match.matchResult &&
        Object.keys(this.match.matchResult.home).length > 0
      ) {
        return this.match.matchResult.home.goalsFor
      }
      return 0
    },
    awayTeamScore (): number {
      if (
        'away' in this.match.matchResult &&
        Object.keys(this.match.matchResult.away).length > 0
      ) {
        return this.match.matchResult.away.goalsFor
      }
      return 0
    },
    homeTeamPlayers (): Array<unknown> {
      if ('players' in this.match.homeTeam) {
        return [...this.match.homeTeam.players].sort(
          (a: any, b: any) => a.number - b.number
        )
      }
      return []
    },
    awayTeamPlayers (): Array<unknown> {
      if ('players' in this.match.awayTeam) {
        return [...this.match.awayTeam.players].sort(
          (a: any, b: any) => a.number - b.number
        )
      }
      return []
    },
    homeTeamBench (): Array<unknown> {
      return this.getBench(this.match.homeTeam)
    },
    awayTeamBench (): Array<unknown> {
      return this.getBench(this.match.awayTeam)
    },
    eventLog (): Array<MatchEvent> {
      return this.filterEvents()
    },
    rawMatchLogEvents (): MatchLogSource[] {
      const match = this.match || {}
      if ('eventLog' in match && match.eventLog != null) {
        return Array.isArray(match.eventLog) ? match.eventLog : Object.values(match.eventLog)
      }
      if ('matchLog' in match && match.matchLog != null && typeof match.matchLog === 'object') {
        return Object.values(match.matchLog)
      }
      return []
    },
    matchLogHeaders (): Array<{ text: string, value: string, sortable?: boolean, width?: string }> {
      return [
        { text: '', value: 'data-table-expand', sortable: false, width: '48px' },
        { text: this.$t('match-log-time'), value: 'timeLabel' },
        { text: this.$t('order'), value: 'order' },
        { text: this.$t('team'), value: 'team' },
        { text: this.$t('player-name'), value: 'player' },
        { text: this.$t('match-events'), value: 'eventLabel' },
        { text: this.$t('match-log-score'), value: 'score' },
        { text: this.$t('match-log-status'), value: 'status' },
        { text: 'logId', value: 'logId' }
      ]
    },
    fullMatchLog (): MatchLogRow[] {
      const grouped = new Map<string, MatchLogSource[]>()
      const solo: MatchLogSource[][] = []

      this.rawMatchLogEvents.forEach((event: MatchLogSource, index: number) => {
        if (!event) return
        const logId = event.logId != null && String(event.logId) !== '' ? String(event.logId) : ''
        if (!logId) {
          solo.push([{ ...event, _soloKey: `solo-${index}` }])
          return
        }
        const rows = grouped.get(logId) || []
        rows.push(event)
        grouped.set(logId, rows)
      })

      const byOrder = (a: MatchLogSource, b: MatchLogSource) =>
        (Number(a.order) || 0) - (Number(b.order) || 0)
      const byTimeThenOrder = (a: MatchLogSource, b: MatchLogSource) => {
        const timeDiff = (Number(a.matchTime) || 0) - (Number(b.matchTime) || 0)
        if (timeDiff !== 0) return timeDiff
        return byOrder(a, b)
      }

      const groups: Array<{ id: string, parent: MatchLogSource, revisions: MatchLogSource[] }> = []
      grouped.forEach((rows, logId) => {
        const ranked = [...rows].sort(byOrder)
        groups.push({
          id: logId,
          parent: ranked[ranked.length - 1],
          revisions: ranked.slice(0, -1)
        })
      })
      solo.forEach((rows) => {
        groups.push({
          id: rows[0]._soloKey || `solo-${groups.length}`,
          parent: rows[0],
          revisions: []
        })
      })
      groups.sort((a, b) => byTimeThenOrder(a.parent, b.parent))

      let home = 0
      let away = 0
      return groups.map((group) => {
        if (this.matchLogCountsAsGoal(group.parent)) {
          const side = this.matchLogTeamSide(group.parent)
          if (side === 0) home += 1
          if (side === 1) away += 1
        }
        const scoreLabel = this.matchLogShowsScore(group.parent) ? `${home}-${away}` : ''
        return {
          ...this.matchLogRow(group.parent, group.id, scoreLabel),
          revisions: group.revisions.map((revision, revisionIndex) => {
            const revisionScore = this.matchLogShowsScore(revision) ? `${home}-${away}` : ''
            return this.matchLogRow(revision, `${group.id}-${revisionIndex}`, revisionScore)
          })
        }
      })
    },
    homeScore () {
      return this.score[0]
    },
    awayScore () {
      return this.score[1]
    }
  },
  methods: {
    openDialog () {
      this.matchReportDialog = !this.matchReportDialog
      const matchResultSetup: MatchResult = {
        home: {
          goalsFor: 0,
          goalsAgainst: 0,
          points: 0
        },
        away: {
          goalsFor: 0,
          goalsAgainst: 0,
          points: 0
        },
        halfTime: {
          home: 0,
          away: 0
        },
        normalTime: {
          home: 0,
          away: 0
        },
        fullTime: {
          home: 0,
          away: 0
        }
      }
      if (
        !('matchResult' in this.match) ||
        !('home' in this.match.matchResult) ||
        Object.keys(this.match.matchResult).length === 0
      ) {
        this.matchResult = matchResultSetup
      } else {
        this.matchResult = {
          ...matchResultSetup,
          ...this.match.matchResult
        }
      }
      if ('players' in this.match.homeTeam) {
        const awayPlayers = ('players' in this.match.awayTeam) ? this.match.awayTeam.players : []
        this.players = this.match.homeTeam.players.concat(
          awayPlayers
        )
      } else {
        this.players = []
      }
      this.teams.push(this.match.homeTeam)
      this.teams.push(this.match.awayTeam)
      this.$store.dispatch('GroupModule/fetchById', this.match.groupId).then(() => {
        const id = this.$store.state.GroupModule.data[this.match.groupId].competitionId
        this.$store.dispatch('CompetitonsModule/fetchById', id)
        const teams = [
          ['id', 'in', [this.match.homeTeam.teamId, this.match.awayTeam.teamId]]
        ]
        this.$store.dispatch('ClubTeamsModule/fetchAndAdd', { where: teams })
      })
      if (this.match.state === 'ON_GOING') {
        this.$store.dispatch('MatchModule/openDBChannel', { where: [['id', '==', this.match.id]] })
          .then(({ streaming, stop }) => {
            streaming.then(() => {
              stop()
            }).catch((error: any) => console.error(error))
          })
          .catch((error: any) => {
            console.error('catch', error)
          })
      }
    },
    closeDialog () {
      this.$store.dispatch('MatchModule/closeDBChannel')
      this.loading = false
      this.matchReportDialog = !this.matchReportDialog
    },
    generateReport (): void {
      this.loading = true
      const id = this.match.id
      const competitionId = this.$store.state.GroupModule.data[this.match.groupId].competitionId
      const promise = new Promise((resolve) => {
        this.$store.dispatch('ClubsModule/fetchAndAdd').then(() => {
          this.$store
            .dispatch('MatchModule/patch', {
              id,
              competitionId: competitionId,
              competitionName: this.$store.state.CompetitonsModule.data[competitionId].name,
              homeTeam: {
                clubId: this.$store.state.ClubTeamsModule.data[this.match.homeTeam.teamId].clubId,
                shortName:
                  this.$store.state.ClubsModule.data[
                    this.$store.state.ClubTeamsModule.data[this.match.homeTeam.teamId].clubId
                  ].shortName,
                bench: this.getBench(this.match.homeTeam)
              },
              awayTeam: {
                clubId: this.$store.state.ClubTeamsModule.data[this.match.awayTeam.teamId].clubId,
                shortName:
                  this.$store.state.ClubsModule.data[
                    this.$store.state.ClubTeamsModule.data[this.match.awayTeam.teamId].clubId
                  ].shortName,
                bench: this.getBench(this.match.awayTeam)
              }
            })
            .then(() => {
              resolve(true)
            })
            .catch((error: any) => {
              showSnackbar(error, { duration: 5000, color: 'danger' })
              this.loading = false
            })
        })
      })
      promise.then(async (status) => {
        if (status) {
          const reportCall = firebase
            .functions()
            .httpsCallable('generateMatchReport')
          await reportCall({ match: this.match.id })
            .then((result) => {
              this.loading = false
              window.open(result.data)
            })
            .catch((error: any) => {
              showSnackbar(error, { duration: 5000, color: 'danger' })
              this.loading = false
            })
        } else {
          this.loading = false
        }
      }).catch((error: any) => {
        showSnackbar(error, { duration: 5000, color: 'danger' })
        this.loading = false
      })
    },
    openReport (): void {
      window.open(`${this.downloadString}`)
    },
    openMatchLog (): void {
      this.expandedMatchLog = []
      this.matchLogDialog = true
    },
    onMatchLogRowClick (item: { id: string, revisions: Array<unknown> }): void {
      if (!item.revisions || item.revisions.length === 0) return
      const open = this.expandedMatchLog.some((row: { id: string }) => row.id === item.id)
      this.expandedMatchLog = open ? [] : [item]
    },
    formatMatchLogTime (time: number): string {
      const seconds = Math.floor(Number(time))
      if (!Number.isFinite(seconds) || seconds < 0) return ''
      const minute = Math.floor(seconds / 60)
      const rest = seconds % 60
      const pad = (value: number) => String(value).padStart(2, '0')
      return `${pad(minute)}:${pad(rest)}`
    },
    matchLogTeamName (event: { teamId?: string }): string {
      if (!event || !event.teamId) return ''
      const team = (this.teams as Array<{ teamId: string, name: string }>).find(
        (item) => item.teamId === event.teamId
      )
      return team ? team.name : ''
    },
    matchLogPlayerName (event: { playerId?: string, letter?: string }): string {
      if (!event) return ''
      if (event.playerId) {
        const player = (this.players as Array<{ id: string, name: string }>).find(
          (item) => item.id === event.playerId
        )
        return player ? player.name : '?'
      }
      if (event.letter) return event.letter
      return ''
    },
    matchLogTeamSide (event: { teamId?: string }): number {
      if (!event || !event.teamId) return -1
      return (this.teams as Array<{ teamId: string }>).findIndex(
        (item) => item.teamId === event.teamId
      )
    },
    matchLogCountsAsGoal (event: { eventType?: string, hidden?: boolean, deleted?: boolean }): boolean {
      if (!event || event.hidden || event.deleted) return false
      return event.eventType === 'goal' || event.eventType === 'goal7meters'
    },
    matchLogShowsScore (event: { eventType?: string }): boolean {
      if (!event || !event.eventType) return false
      return ['goal', 'goal7meters', 'matchStarted', 'periodEnded', 'matchEnded'].includes(event.eventType)
    },
    matchLogStatus (event: { hidden?: boolean, edited?: boolean, deleted?: boolean }): string {
      if (!event) return ''
      const labels = []
      if (event.hidden) labels.push(this.$t('match-log-hidden'))
      if (event.edited) labels.push(this.$t('match-log-edited'))
      if (event.deleted) labels.push(this.$t('match-log-deleted'))
      return labels.join(', ')
    },
    matchLogRow (event: MatchLogSource, id: string, score: string): MatchLogRow {
      return {
        id,
        timeLabel: this.formatMatchLogTime(event.matchTime),
        order: event.order,
        team: this.matchLogTeamName(event),
        player: this.matchLogPlayerName(event),
        eventLabel: event.eventType ? this.$t(event.eventType) : '',
        score,
        status: this.matchLogStatus(event),
        logId: event.logId != null ? String(event.logId) : ''
      }
    },
    filterEvents (): Array<MatchEvent> {
      if (!('eventLog' in this.match) && !('matchLog' in this.match)) return []
      /* eslint-disable no-prototype-builtins */
      const log = ('eventLog' in this.match) ? this.match.eventLog : Object.entries(this.match.matchLog).map(([key, value]) => ({ ...value as Record<string, unknown> }))
      const events = log.filter(
        (e: { hidden: boolean; edited: boolean; deleted: boolean }) =>
          e.hidden !== true && e.edited !== true && e.deleted !== true
      )
      return events.sort((a: MatchEvent, b: MatchEvent) => {
        if (a.matchTime === b.matchTime) {
          // If two logEvents have the same matchTime, then the one who has lowest order wins
          return a.order - b.order
        } else {
          // If two logEvents have different matchTime, then the one who has lowest matchTime wins
          return a.matchTime - b.matchTime
        }
      })
    },
    getPlayerName (who: { playerId: string; eventType: string }): string {
      if (!('playerId' in who)) return
      try {
        return this.players[
          this.players.findIndex((p: { id: string }) => p.id === who.playerId)
        ].name
      } catch (e) {
        return '?'
      }
    },
    getTeam (data: MatchEvent, side: number): boolean {
      if (!('teamId' in data)) return
      const team = this.teams.findIndex(
        (p: { teamId: string }) => p.teamId === data.teamId
      )
      return team === side
    },
    getTeamName (teamId: string): string {
      if (teamId === undefined) return
      return this.teams[
        this.teams.findIndex((p: { teamId: string }) => p.teamId === teamId)
      ].name
    },
    getBench (team: any) {
      if ('bench' in team) {
        if (typeof team.bench === 'object') {
          const bench = Object.entries(team.bench as unknown).map(key => ({ ...key[1] }))
          return bench.sort((a, b) => a.letter.localeCompare(b.letter))
        }
        return [...team.bench].sort((a, b) => a.letter.localeCompare(b.letter))
      }
      return [{ letter: 'A', name: '' }, { letter: 'B', name: '' }, { letter: 'C', name: '' }, { letter: 'D', name: '' }]
    },
    isGoal (eventType: string) {
      const types = ['goal', 'goal7meters']
      return types.includes(eventType)
    },
    calculateScore (event: MatchEvent) {
      const team = this.teams.findIndex(
        (p: { teamId: string }) => p.teamId === event.teamId
      )
      if (this.isGoal(event.eventType)) {
        ++this.score[team]
        return `${this.score[0]} - ${this.score[1]}`
      }
    },
    alignText (event: MatchEvent, side: number, i: number) {
      if (!('teamId' in event) || i === 0) return 0
      const team = this.teams.findIndex(
        (p: { teamId: string }) => p.teamId === event.teamId
      )
      return (team === side && i < this.eventLog.length)
    },
    nearestMinute (time: number) {
      if (time === undefined || time < 60) return 1
      return Math.ceil((time * 1000) / 60000)
    },
    saveMatchResult () {
      this.saving = !this.saving
      const id = this.match.id
      this.matchResult.home.goalsAgainst = this.matchResult.away.goalsFor
      this.matchResult.away.goalsAgainst = this.matchResult.home.goalsFor
      this.matchResult.fullTime = {
        home: this.matchResult.home.goalsFor,
        away: this.matchResult.away.goalsFor
      }
      this.matchResult.normalTime = {
        home: this.matchResult.home.goalsFor,
        away: this.matchResult.away.goalsFor
      }

      if (parseInt(this.matchResult.home.goalsFor) > parseInt(this.matchResult.away.goalsFor)) {
        this.matchResult.home.points = 2
        this.matchResult.away.points = 0
      } else if (parseInt(this.matchResult.home.goalsFor) < parseInt(this.matchResult.away.goalsFor)) {
        this.matchResult.home.points = 0
        this.matchResult.away.points = 2
      } else if (parseInt(this.matchResult.home.goalsFor) === parseInt(this.matchResult.away.goalsFor)) {
        this.matchResult.home.points = 1
        this.matchResult.away.points = 1
      } else {
        this.matchResult.home.points = 0
        this.matchResult.away.points = 0
      }

      this.$store
        .dispatch('MatchModule/patch', {
          id,
          matchResult: this.matchResult
        })
        .then(() => {
          showSnackbar('Match result saved.', { color: 'success' })
          this.saving = !this.saving
        })
    },
    isIterable (value: any) {
      return Object.keys(Object(value)).length > 0
    }
  }
}
